// YearComparison.jsx
// ─────────────────────────────────────────────────────────────
// Year-over-Year comparison panel showing:
//  1. Annual spend diff (current year vs previous year)
//  2. Spec-wise price comparison table:
//     For each spec that appears in BOTH years, show:
//     prev year price → current year price → % change
//     (same logic as comparing "i5 laptop 2025 vs i5 laptop 2026")
// ─────────────────────────────────────────────────────────────
import styles from "./SourcingIntelligence.module.css";
import { Icons, parseINR } from "./SourcingIntelligence";

export default function YearComparison({ currentYear, prevYear, allYears }) {
  const currSpend = parseINR(currentYear.annual_spend.total_spend);
  const prevSpend = parseINR(prevYear.annual_spend.total_spend);
  const diff = currSpend - prevSpend;
  const pct = prevSpend > 0 ? ((diff / prevSpend) * 100) : 0;
  const isUp = diff > 0;
  const isFlat = diff === 0;

  const arrowCls = isFlat ? styles.yoyArrowFlat : isUp ? styles.yoyArrowUp : styles.yoyArrowDown;

  const fmtShort = v => {
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${Number(v).toLocaleString("en-IN")}`;
  };

  // ── Spec-wise cross-year comparison ──────────────────────
  // For every unique spec: find the avg unit_price in prevYear and currentYear
  // (avg because same spec may have multiple POs in a year)
  const allSpecs = [...new Set([
    ...currentYear.itemwise.map(i => i.specification),
    ...prevYear.itemwise.map(i => i.specification),
  ])];

  const getYearAvgPrice = (yearData, spec) => {
    const matches = yearData.itemwise.filter(i => i.specification === spec);
    if (matches.length === 0) return null;
    const weighted = matches.reduce(
      (acc, m) => {
        const u = parseINR(m.unit_price);
        const q = Number(m.quantity);
        const w = q > 0 ? q : 1;
        acc.sum += u * w;
        acc.w += w;
        return acc;
      },
      { sum: 0, w: 0 }
    );
    const avg = weighted.w > 0 ? weighted.sum / weighted.w : 0;
    return { avg, count: matches.length, vendors: [...new Set(matches.map(m => m.vendor_name).filter(Boolean))] };
  };

  const renderVendors = (vendors) => {
    const unique = Array.isArray(vendors) ? [...new Set(vendors.filter(Boolean))] : [];
    const title = unique.join("\n");
    return (
      <div className={styles.vendorPills} title={title}>
        {unique.map((v) => (
          <span key={v} className={styles.vendorPill}>
            {v}
          </span>
        ))}
      </div>
    );
  };

  const specRows = allSpecs.map(spec => {
    const curr = getYearAvgPrice(currentYear, spec);
    const prev = getYearAvgPrice(prevYear, spec);
    return { spec, curr, prev };
  }).filter(r => r.curr && r.prev); // only specs in BOTH years

  const specsOnlyInCurr = allSpecs.filter(s => getYearAvgPrice(currentYear, s) && !getYearAvgPrice(prevYear, s));
  const specsOnlyInPrev = allSpecs.filter(s => !getYearAvgPrice(currentYear, s) && getYearAvgPrice(prevYear, s));

  return (
    <div className={styles.yoyCard}>
      <div className={styles.yoyTitle}>
        <div className={styles.sectionDot} />
        Year-over-Year Comparison: {currentYear.year} vs {prevYear.year}
      </div>

      {/* Spend comparison */}
      <div className={styles.yoyGrid}>
        <div className={styles.yoyBlock}>
          <div className={styles.yoyBlockYear}>FY {prevYear.year} (Previous)</div>
          <div className={styles.yoyBlockVal}>₹{prevYear.annual_spend.total_spend}</div>
          <div className={styles.yoyBlockSub}>
            {prevYear.itemwise.length} POs &nbsp;·&nbsp;
            {[...new Set(prevYear.itemwise.map(i => i.vendor_name))].length} vendors
          </div>
        </div>

        <div className={`${styles.yoyArrow} ${arrowCls}`}>
          <div className={styles.yoyArrowVal}>
            {isUp ? <Icons.TrendUp size={24} /> : isFlat ? <Icons.Equal size={24} /> : <Icons.TrendDown size={24} />}
            {Math.abs(pct).toFixed(1)}%
          </div>
          <div className={styles.yoyArrowLbl}>
            {isFlat ? "No Change" : isUp ? `+${fmtShort(Math.abs(diff))} higher` : `${fmtShort(Math.abs(diff))} lower`}
          </div>
        </div>

        <div className={styles.yoyBlock}>
          <div className={styles.yoyBlockYear}>FY {currentYear.year} (Current)</div>
          <div className={styles.yoyBlockVal}>₹{currentYear.annual_spend.total_spend}</div>
          <div className={styles.yoyBlockSub}>
            {currentYear.itemwise.length} POs &nbsp;·&nbsp;
            {[...new Set(currentYear.itemwise.map(i => i.vendor_name))].length} vendors
          </div>
        </div>
      </div>

      {/* ── Spec-wise cross-year price table ─────────────── */}
      {specRows.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 14, fontWeight: 800, color: "var(--text)",
            marginBottom: 4, display: "flex", alignItems: "center", gap: 8,
          }}>
            <Icons.History size={14} />
            Same-Specification Price Comparison
            <span className={`${styles.badge} ${styles.badgePrimary}`} style={{ fontSize: 11 }}>
              {specRows.length} spec{specRows.length !== 1 ? "s" : ""} matched
            </span>
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, marginBottom: 14 }}>
            Only compares specs that exist in BOTH years — no cross-spec comparison.
            e.g. i5 laptop vs i5 laptop, not i5 vs i7. Avg price is qty-weighted.
          </div>

          <div className={styles.tableScroll}>
            <table className={styles.poTable}>
              <thead>
                <tr>
                  <th className={styles.th} style={{ minWidth: 300 }}>Specification</th>
                  <th className={styles.th}>FY {prevYear.year} Avg Price</th>
                  <th className={styles.th}>FY {currentYear.year} Avg Price</th>
                  <th className={styles.th}>Change</th>
                  <th className={styles.th}>Diff Amount</th>
                  <th className={styles.th}>Vendors ({prevYear.year})</th>
                  <th className={styles.th}>Vendors ({currentYear.year})</th>
                </tr>
              </thead>
              <tbody>
                {specRows.map(({ spec, curr, prev }) => {
                  const change = curr.avg - prev.avg;
                  const changePct = prev.avg > 0 ? ((change / prev.avg) * 100) : 0;
                  const isInc = change > 0;
                  const isDec = change < 0;
                  const dirCls = isInc ? styles.dirIncreased : isDec ? styles.dirDecreased : styles.dirSame;

                  return (
                    <tr key={spec}>
                      <td className={styles.td}>
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: "var(--text)",
                          maxWidth: 340, lineHeight: 1.4,
                        }}>
                          {spec}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                          {prev.count} PO{prev.count !== 1 ? "s" : ""} in {prevYear.year}&nbsp;·&nbsp;
                          {curr.count} PO{curr.count !== 1 ? "s" : ""} in {currentYear.year}
                        </div>
                      </td>
                      <td className={styles.td}>
                        <span style={{ fontWeight: 800, fontSize: 15 }}>
                          ₹{Number(prev.avg).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </span>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>avg / unit</div>
                      </td>
                      <td className={styles.td}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: "var(--primary)" }}>
                          ₹{Number(curr.avg).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </span>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>avg / unit</div>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.priceDirectionChip} ${dirCls}`}>
                          {isInc ? <Icons.TrendUp size={12} /> : isDec ? <Icons.TrendDown size={12} /> : <Icons.Equal size={12} />}
                          &nbsp;{changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span style={{
                          fontWeight: 800, fontSize: 14,
                          color: isInc ? "var(--danger)" : isDec ? "var(--success)" : "var(--neutral)",
                        }}>
                          {change >= 0 ? "+" : ""}₹{Number(Math.abs(change)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </span>
                      </td>
                      <td className={styles.td}>
                        {renderVendors(prev.vendors)}
                      </td>
                      <td className={styles.td}>
                        {renderVendors(curr.vendors)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New specs (not in prev year) */}
      {specsOnlyInCurr.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--warning)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Icons.Info size={13} />
            New Specifications in {currentYear.year} (no prior year data for comparison)
          </div>
          {specsOnlyInCurr.map(spec => (
            <div key={spec} style={{
              background: "var(--warning-bg)", border: "1px solid var(--warning-border)",
              borderRadius: "var(--radius-xs)", padding: "8px 12px", marginBottom: 6,
              fontSize: 13, fontWeight: 600, color: "var(--text)",
            }}>
              {spec}
            </div>
          ))}
        </div>
      )}

      {/* Specs discontinued */}
      {specsOnlyInPrev.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--neutral)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Icons.Info size={13} />
            Specifications from {prevYear.year} not purchased in {currentYear.year}
          </div>
          {specsOnlyInPrev.map(spec => (
            <div key={spec} style={{
              background: "var(--neutral-bg)", border: "1px solid var(--neutral-border)",
              borderRadius: "var(--radius-xs)", padding: "8px 12px", marginBottom: 6,
              fontSize: 13, fontWeight: 600, color: "var(--text)",
            }}>
              {spec}
            </div>
          ))}
        </div>
      )}

      {specRows.length === 0 && specsOnlyInCurr.length === 0 && specsOnlyInPrev.length === 0 && (
        <div style={{ marginTop: 16 }}>
          <span className={`${styles.badge} ${styles.badgeNeutral}`}>
            No overlapping specifications found between {prevYear.year} and {currentYear.year}.
          </span>
        </div>
      )}
    </div>
  );
}
